<script setup>
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { t } = useI18n();
const store = useStore();

// Props
const props = defineProps({
    potentialMistake: {
        type: Object,
        default: () => ({})
    },
    episode: {
        type: Object,
        required: false,
        default: null
    }
});

// Emits
const emit = defineEmits(['save', 'close']);

// State
const skipStaff = ref(false);

// Computed
const staffName = computed(() => {
    return props.potentialMistake.staff?.ja || props.potentialMistake.staff?.en;
});

const isMissingId = computed(() => {
    // Check if all matches have the same ja/en as the original (meaning only ID is missing)
    if (!props.potentialMistake.matches || props.potentialMistake.matches.length === 0) {
        return false;
    }
    
    const originalJa = props.potentialMistake.staff?.ja;
    const originalEn = props.potentialMistake.staff?.en;
    
    return props.potentialMistake.matches.every(match => {
        const matchJa = match.staff.ja;
        const matchEn = match.staff.en;
        
        // If original has ja, check if match has same ja
        if (originalJa && matchJa) {
            return originalJa === matchJa;
        }
        
        // If original has en, check if match has same en
        if (originalEn && matchEn) {
            return originalEn === matchEn;
        }
        
        return false;
    });
});

// Methods
const highlight = () => {
    const staff = props.potentialMistake.staff;
    const episode = props.episode;

    if (staff.isHighlighted) {
        return;
    }

    if (store.state.selectedEpisode !== episode) {
        store.commit('selectEpisode', {episode: episode, scroll: true});
    }

    staff.isHighlighted = true;

    nextTick(() => {
        if (document.querySelector('.highlighted')) {
            const y = document.querySelector('.highlighted').getBoundingClientRect().top
                + window.pageYOffset
                - window.innerHeight / 2;
            window.scrollTo({top: y});
        }
    });

    setTimeout(() => {
        delete staff.isHighlighted;
    }, 1500);
};

const save = () => {
    emit('save', { goNext: true, skipStaff: skipStaff.value });
    emit('close');
};

const close = () => {
    store.commit('addConfirm', {
        id: "potential-mistake-close",
        title: "Stop Miscredit Check",
        text: "This will stop the Miscredit Check without saving progress. Are you sure you want to close it?",
        callback: (result) => {
            if (!result) return;

            emit('save', { goNext: false, skipStaff: skipStaff.value });
            emit('close');
        }
    });
};
</script>

<template>
    <div class="fixed top-0 left-0 p-10 w-full h-full flex items-end bg-gradient-to-t from-black/50 to-60% pointer-events-none" :style="{ zIndex: potentialMistake.zIndex || 50 }">
        <div class="w-full flex justify-center pointer-events-none">
            <Transition appear enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100">
                <div
                    class="dialog bg-app-surface1 rounded-xl overflow-hidden w-full shadow-2xl max-h-[90vh] overflow-y-auto transition-all duration-100 ease-out flex flex-col pointer-events-auto"
                    role="alertdialog"
                    aria-modal="true"
                >
                    <div class="flex items-center justify-between px-6 py-4 border-b border-app-border">
                        <h2 class="text-lg font-semibold text-app-text">Miscredit Check</h2>
                        <button
                            class="inline-flex items-center justify-center w-8 h-8 rounded-full text-app-text-soft hover:bg-app-border transition-colors"
                            @click="close"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex-1 px-6 py-5">
                        <div v-if="potentialMistake.isLoading" class="w-full flex flex-col gap-4 py-10 justify-center items-center text-slate-400">
                            <i class="text-5xl">
                                <font-awesome-icon icon="fa-solid fa-circle-notch" spin />
                            </i>
                            <p>Checking for potential miscredits...</p>
                        </div>

                        <div v-else>
                            <p v-if="isMissingId" class="text-left break-words leading-relaxed text-app-text2">
                                The ID is potentially missing for <strong>{{ staffName }}</strong>
                                <template v-if="potentialMistake.staff.en && potentialMistake.staff.ja !== potentialMistake.staff.en"> ({{ potentialMistake.staff.en }})</template>.
                                Possible ID<template v-if="potentialMistake.matches.length > 1">s</template>:
                                <span v-for="(match, index) in potentialMistake.matches" :key="index">
                                    <a
                                        :href="`/${match.staff.isStudio ? 'studio' : 'person'}/${match.staff.id}`"
                                        target="_blank"
                                        class="text-keyframe hover:underline font-semibold"
                                    >{{ match.staff.id }}</a><span v-if="index < potentialMistake.matches.length - 1">, </span>
                                </span>.

                                <button class="ml-2 !inline !text-xs !px-2 !py-1.5 btn btn-xs btn-keyframe !rounded-full" @click="highlight">
                                    Highlight row
                                </button>
                            </p>
                            
                            <p v-else class="text-left break-words leading-relaxed text-app-text2">
                                <i18n-t keypath="notifications.potentialMistake.detectedFor" tag="span">
                                    <template #name>
                                        <strong>{{ staffName }}</strong>
                                    </template>
                                    <template #matches>
                                        <span v-for="(match, index) in potentialMistake.matches" :key="index">
                                            <strong>{{ match.staff.ja || match.staff.en }}</strong>
                                            <template v-if="match.staff.ja && match.staff.en"> ({{ match.staff.en }})</template>
                                            <template v-if="match.staff.id"> (<a
                                                    :href="`/${match.staff.isStudio ? 'studio' : 'person'}/${match.staff.id}`"
                                                    target="_blank"
                                                    class="text-keyframe hover:underline font-bold"
                                                >{{ match.staff.id }}</a>)</template>
                                            <span v-if="index < potentialMistake.matches.length - 1">, </span>
                                        </span>
                                    </template>
                                    <template #highlightButton>
                                        <button class="!inline !text-xs !px-2 !py-1.5 btn btn-xs btn-keyframe !rounded-full" @click="highlight">
                                            Highlight row
                                        </button>
                                    </template>
                                </i18n-t>
                            </p>

                            <p class="text-left break-words leading-relaxed text-app-text2 mt-3">
                                <i18n-t keypath="notifications.potentialMistake.checkCredits" tag="span">
                                    <template #episode>
                                        <strong>{{ potentialMistake.episode.name }}</strong>
                                    </template>
                                </i18n-t><br />
                                {{ t('notifications.potentialMistake.instructions') }}
                            </p>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center justify-end gap-3 px-6 py-1" v-if="!potentialMistake.isLoading">
                        <label class="inline-flex items-center gap-2 text-app-text2">
                            <input type="checkbox" class="checkbox" v-model="skipStaff" />
                            <span>Don't show this staff again</span>
                        </label>
                    </div>

                    <div class="flex gap-2 pt-3 px-6 py-6 justify-end" v-if="!potentialMistake.isLoading">
                        <button class="btn btn-default !rounded-full" @click="close">
                            <font-awesome-icon icon="fa-solid fa-times" />
                            {{ t('notifications.potentialMistake.stop') }}
                        </button>
                        <button class="btn btn-primary !rounded-full" @click="save">
                            <font-awesome-icon icon="fa-solid fa-play" />
                            {{ t('notifications.potentialMistake.next') }}
                        </button>
                    </div>
                </div>
            </Transition>
        </div>
    </div>
</template>